import { cn } from 'lib/utils'
import { EyeIcon, EyeOffIcon, type LucideIcon, SearchIcon } from 'lucide-react'
import React, { forwardRef, type ReactNode, useState } from 'react'
import { Input as ShadInput } from './base/input'
import { Button } from './button'
import { Label } from './label'

export interface VariantInputProps extends React.ComponentProps<typeof ShadInput> {
  label?: ReactNode
  helperText?: string | React.ReactNode
  startIcon?: React.ComponentType<{ size: number; className?: string }> | LucideIcon
  endIcon?: React.ReactNode
  endIconClassName?: string
  variant?: 'default' | 'ghost'
  errorText?: string
  error?: React.ReactNode
  inputClassName?: string
}

const Input = forwardRef<HTMLInputElement, VariantInputProps>(
  (
    {
      label,
      helperText,
      startIcon,
      endIcon,
      endIconClassName,
      variant = 'default',
      errorText,
      error,
      ...props
    },
    ref,
  ) => {
    const StartIcon = startIcon
    const EndIcon = endIcon
    const variantClasses = {
      ghost:
        'border-none rounded-none focus:ring-0 [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none',
      default: 'border rounded-md bg-background',
    }

    const inputClasses = cn(
      'grid items-center gap-1 shadow-none',
      (props.type === 'url' || props.inputMode === 'url') &&
        'font-jetbrains tracking-normal normal-case',
      startIcon ? 'pl-10' : '',
      props.className,
      endIcon && props.type !== 'number' ? 'pr-8' : '',
      'peer',
      variantClasses[variant],
      props.disabled ? 'bg-muted text-muted-foreground pointer-events-none' : '',
      error || errorText
        ? variant === 'ghost'
          ? 'outline outline-1 -outline-offset-1 outline-destructive'
          : 'border-destructive'
        : '',
    )

    const renderHelperText = () => {
      if (typeof helperText === 'string') {
        return <p className="text-p-400 text-foreground/50">{helperText}</p>
      }
      return helperText
    }

    return (
      <div className={cn('grid items-center gap-1.5', props.className)}>
        {label && <Label htmlFor={props.name}>{label}</Label>}
        <div className="relative">
          <ShadInput ref={ref} {...props} className={cn(inputClasses, props.inputClassName)} />
          {StartIcon && (
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground transition-colors peer-focus:text-foreground">
              <StartIcon size={18} />
            </div>
          )}
          {EndIcon && (
            <div
              className={cn(
                'absolute right-1 top-1/2 transform -translate-y-1/2',
                endIconClassName,
              )}
            >
              {EndIcon}
            </div>
          )}
        </div>
        {renderHelperText()}
        {errorText || typeof error === 'string' ? (
          <p className="text-s-400 text-destructive mt-1">{errorText || error}</p>
        ) : null}
      </div>
    )
  },
)
Input.displayName = 'Input'

export interface PasswordInputProps extends VariantInputProps {
  hideToggle?: boolean
}

const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
  ({ className, endIcon, hideToggle, ...props }, ref) => {
    const [showPassword, setShowPassword] = useState(false)
    const disabled = props.value === '' || props.value === undefined || props.disabled
    const shouldHideToggle = hideToggle ?? (disabled && !props.value)

    return (
      <div className="relative">
        <Input
          type={showPassword ? 'text' : 'password'}
          className={cn('hide-password-toggle', className)}
          ref={ref}
          disabled={disabled}
          endIcon={
            shouldHideToggle ? (
              endIcon
            ) : (
              <div className="flex items-center gap-1">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="hover:bg-transparent"
                  onClick={() => setShowPassword((prev) => !prev)}
                >
                  {showPassword ? (
                    <EyeIcon className="h-4 w-4" aria-hidden="true" />
                  ) : (
                    <EyeOffIcon className="h-4 w-4" aria-hidden="true" />
                  )}
                  <span className="sr-only">
                    {showPassword ? 'Hide password' : 'Show password'}
                  </span>
                </Button>
                {endIcon}
              </div>
            )
          }
          {...props}
        />

        {/* hides browsers password toggles */}
        <style>{`
					.hide-password-toggle::-ms-reveal,
					.hide-password-toggle::-ms-clear {
						visibility: hidden;
						pointer-events: none;
						display: none;
					}
				`}</style>
      </div>
    )
  },
)
PasswordInput.displayName = 'PasswordInput'

export type SearchInputProps = VariantInputProps

const SearchInput = React.forwardRef<HTMLInputElement, VariantInputProps>(
  ({ className, ...props }, ref) => (
    <Input
      {...props}
      className={cn('font-inter text-s-400 normal-case tracking-normal', className)}
      type="search"
      ref={ref}
      startIcon={SearchIcon}
    />
  ),
)

SearchInput.displayName = 'Search'

export { SearchInput }

export { Input, PasswordInput }
